import { ExternalLink, ShieldCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GRANULARITY, fmtDate } from "./useSciPreview";

// Small "source" chip: opens the verbatim quote, publisher, retrieval date, entry year and data level.
export function EvidenceChip({ claim, testId, subtle }) {
  const s = claim?.source || {};
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} aria-label={`Source: ${s.publisher || "official source"}`}
          className={subtle
            ? "ml-1 inline-flex h-5 w-5 translate-y-[-1px] items-center justify-center rounded-full align-middle text-stone-400 transition-colors hover:bg-emerald-50 hover:text-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
            : "inline-flex items-center gap-1 rounded-full border border-emerald-700/25 bg-emerald-50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-800 transition-colors hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"}>
          <ShieldCheck className="h-3 w-3" aria-hidden="true" />{!subtle && " Source"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-sm" data-testid={`${testId}-popover`}>
        <p className="font-mono text-[10px] uppercase tracking-wider text-stone-500">Exact wording on the official page</p>
        <blockquote className="mt-1 border-l-2 border-emerald-700 pl-3 text-stone-800 italic">“{claim.quote}”</blockquote>
        <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs text-stone-600">
          <dt className="text-stone-400">Publisher</dt><dd>{s.publisher}</dd>
          <dt className="text-stone-400">Checked</dt><dd>{fmtDate(s.retrieved_at)}</dd>
          <dt className="text-stone-400">Applies to</dt><dd>{GRANULARITY[claim.granularity] || claim.granularity}</dd>
          {claim.entry_year && (<><dt className="text-stone-400">Year</dt><dd>{claim.entry_year}</dd></>)}
        </dl>
        {s.url && (
          <a href={s.url} target="_blank" rel="noreferrer" data-testid={`${testId}-link`}
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#002147] underline underline-offset-2">
            Open source page <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        )}
      </PopoverContent>
    </Popover>
  );
}

// One control for 3+ sources: every source keeps its exact wording, publisher, date and link.
export function SourcesGroup({ claims, testId }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} aria-label={`${claims.length} sources for this statement`}
          className="ml-1 inline-flex translate-y-[-1px] items-center gap-1 whitespace-nowrap rounded-full border border-stone-300 px-1.5 py-px align-middle font-sans text-[11px] font-normal not-italic leading-4 tracking-normal [word-spacing:normal] text-stone-500 transition-colors hover:border-emerald-700 hover:text-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700">
          <ShieldCheck className="h-3 w-3" aria-hidden="true" /><span>{claims.length}&nbsp;sources</span>
        </button>
      </PopoverTrigger>
      <PopoverContent collisionPadding={8} className="flex w-[min(24rem,calc(100vw-1rem))] max-h-[min(28rem,var(--radix-popover-content-available-height))] flex-col p-0 text-sm" data-testid={`${testId}-popover`}>
        <p className="border-b border-stone-100 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">{claims.length} sources · exact wording on each official page</p>
        <div className="min-h-0 overflow-y-auto">
          <ol className="divide-y divide-stone-100">{claims.map((c, i) => {
            const s = c.source || {};
            return (
              <li key={i} className="px-4 py-3" data-testid={`${testId}-item-${i}`}>
                <p className="text-[11px] font-medium text-stone-700"><span className="mr-1 font-mono text-stone-400">{i + 1}.</span>{s.publisher}</p>
                {c.quote && <blockquote className="mt-1 border-l-2 border-emerald-700 pl-3 text-[13px] italic text-stone-800">“{c.quote}”</blockquote>}
                <p className="mt-1 text-[11px] text-stone-500">Checked {fmtDate(s.retrieved_at)} · {GRANULARITY[c.granularity] || c.granularity}{c.claim_id && <span className="font-mono"> · {c.claim_id.slice(0, 8)}</span>}</p>
                {s.url && <a href={s.url} target="_blank" rel="noreferrer" data-testid={`${testId}-link-${i}`}
                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[#002147] underline underline-offset-2">Open source page <ExternalLink className="h-3 w-3" aria-hidden="true" /></a>}
              </li>
            );
          })}</ol>
        </div>
      </PopoverContent>
    </Popover>
  );
}
